import React, { useState, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { id: 'waiver', label: 'Waiver' },
  { id: 'permission_slip', label: 'Permission Slip' },
  { id: 'participation_agreement', label: 'Participation Agreement' },
  { id: 'info_sheet', label: 'Information Sheet' },
  { id: 'other', label: 'Other' },
];

export default function DocumentCreate() {
  const { institutionId } = useParams();
  const [searchParams] = useSearchParams();
  const activityId = searchParams.get('activity');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    title: '',
    content: '',
    document_type: 'waiver',
    requires_acknowledgement: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...form,
        institution_id: institutionId,
        activity_id: activityId || undefined,
      };
      const res = await axios.post(`${API}/api/documents/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document created');
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) { navigate('/auth'); return null; }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Create Document</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Field Trip Waiver"
              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
              data-testid="doc-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>Type</label>
            <div className="flex flex-wrap gap-2">
              {DOC_TYPES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, document_type: t.id }))}
                  className="px-3 py-1.5 rounded-full text-sm transition-all"
                  style={{
                    backgroundColor: form.document_type === t.id ? 'var(--lk-sage-muted, #EDF1E8)' : 'var(--fy-surface-card)',
                    border: `1px solid ${form.document_type === t.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                    color: form.document_type === t.id ? 'var(--lk-sage)' : 'var(--fy-text-secondary)',
                  }}
                  data-testid={`doc-type-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>Content</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Document content — waivers, agreements, instructions..."
              rows={8}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
              data-testid="doc-content-input"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, requires_acknowledgement: !f.requires_acknowledgement }))}
              className="w-10 h-6 rounded-full relative transition-all"
              style={{ backgroundColor: form.requires_acknowledgement ? 'var(--lk-sage)' : 'var(--fy-border-subtle)' }}
              data-testid="requires-ack-toggle"
            >
              <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all"
                style={{ left: form.requires_acknowledgement ? '18px' : '2px' }} />
            </button>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Require acknowledgement</p>
              <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Participants must acknowledge before joining</p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || !form.title.trim()}
            className="w-full h-12 rounded-full text-base font-medium"
            style={{ backgroundColor: 'var(--lk-sage)', color: 'white', opacity: submitting ? 0.6 : 1 }}
            data-testid="create-document-submit"
          >
            {submitting ? 'Creating...' : 'Create Document'}
          </Button>
        </form>
      </main>
    </div>
  );
}
